(() => {
  const issueData = {
    residential: [
      { id:'bills', label:'Optimise my energy costs', title:'Your energy profile can reveal the clearest savings opportunities.', body:'We connect solar production, evening consumption, tariff structure, controlled loads and system performance to identify the improvements with the greatest potential value.', signals:[['Daytime solar',74],['Evening demand',88],['Tariff fit',48]], evidence:'Recent bills, inverter generation and interval usage data.', action:'Compare generation, export and grid purchases across a typical day.' },
      { id:'export', label:'Increase my solar value', title:'Your daytime solar surplus can deliver more value across the day.', body:'We assess when surplus occurs, what the home uses later and how storage, load shifting or tariff selection could increase the value of each generated kilowatt-hour.', signals:[['Solar surplus',92],['Later consumption',76],['Storage fit',66]], evidence:'Export totals, time-of-use consumption and battery operating assumptions.', action:'Quantify usable surplus and the load that could be served later.' },
      { id:'performance', label:'Maximise my solar performance', title:'Turn generation and consumption data into a clear performance picture.', body:'We benchmark solar output and household demand while accounting for shading, inverter settings, export settings and seasonal variation.', signals:[['Generation insight',84],['Load profile',70],['Export settings',44]], evidence:'Inverter history, system size, installation details and comparable bills.', action:'Benchmark actual production and consumption against the system design.' },
      { id:'outages', label:'Strengthen backup readiness', title:'A well-designed backup plan keeps priority loads at the centre.', body:'We map critical loads, inverter output, phase configuration, starting currents and preferred operating duration to shape a practical storage and backup design.', signals:[['Critical loads',88],['Backup duration',72],['Site configuration',80]], evidence:'Switchboard configuration, essential-load list and required operating duration.', action:'Define what should stay on, for how long and under the preferred operating conditions.' },
      { id:'future', label:'Plan for EVs and new loads', title:'Plan today’s energy system around tomorrow’s demand.', body:'EV charging, larger air conditioning, pools, heat pumps and electrification can be incorporated into solar, inverter, battery and switchboard planning.', signals:[['Future load',90],['Site capacity',68],['Charging window',76]], evidence:'Planned equipment, usage schedule and existing supply capacity.', action:'Model the future load before finalising the system size.' },
      { id:'unsure', label:'Help me choose the right solution', title:'Your energy profile points to the right starting solution.', body:'We map bills, usage timing, existing equipment, property characteristics and priorities to determine the most useful combination of solar, storage, controls and energy-management services.', signals:[['Usage profile',82],['Site potential',62],['Goal clarity',78]], evidence:'Recent bills, property details and the outcome you want.', action:'Complete a broad diagnostic before selecting the technology.' }
    ],
    commercial: [
      { id:'cost', label:'Reduce operating energy costs', title:'Your bill contains opportunities for targeted improvement.', body:'We separate retail energy, network volume, demand, capacity, metering and environmental charges so each controllable component receives the right response.', signals:[['Energy volume',74],['Network opportunity',82],['Tariff fit',72]], evidence:'Twelve months of bills, tariff details and interval data.', action:'Break the bill into controllable cost drivers before modelling equipment.' },
      { id:'demand', label:'Optimise demand charges', title:'Identifying short peak events can unlock recurring savings opportunities.', body:'We identify when maximum demand occurs, the equipment involved, how the tariff calculates it and how operational control or storage could improve the result.', signals:[['Peak demand',94],['Peak duration',64],['Control potential',78]], evidence:'Interval data, demand tariff rules and equipment operating schedules.', action:'Locate the peak events and test operational and storage responses.' },
      { id:'roof', label:'Activate unused roof space', title:'Match roof potential with the loads that create the greatest value.', body:'We connect daytime consumption, export settings, electrical capacity, roof condition and future load to determine a commercially useful solar size.', signals:[['Roof potential',86],['Daytime load',72],['Export capacity',54]], evidence:'Roof/site information, load profile, switchboard and network settings.', action:'Model viable generation against onsite consumption and export conditions.' },
      { id:'resilience', label:'Strengthen business continuity', title:'A resilient energy plan starts with the operations that matter most.', body:'We identify critical processes, preferred continuity, essential loads, restart requirements and the useful duration of protection for the business.', signals:[['Critical operations',92],['Continuity value',86],['Backup duration',70]], evidence:'Critical-load schedule, outage history and operational continuity needs.', action:'Design resilience around business continuity and priority operations.' },
      { id:'growth', label:'Plan for expansion and EVs', title:'Build energy infrastructure that supports the next stage of growth.', body:'New equipment, refrigeration, production, tenancies and EV charging can be planned together with generation, storage and controls to manage future demand efficiently.', signals:[['Growth load',92],['Supply capacity',80],['Peak interaction',84]], evidence:'Expansion plan, equipment schedule, charging profile and supply capacity.', action:'Create a staged energy plan that supports growth and manages peak demand.' },
      { id:'investment', label:'Build a clear investment case', title:'A strong energy investment case makes assumptions and alternatives clear.', body:'We compare the base case with solar, storage and combined scenarios, including tariff sensitivity, degradation, operating strategy, available incentives and implementation requirements.', signals:[['Data quality',82],['Scenario model',91],['Scenario sensitivity',75]], evidence:'Bills, interval data, capex assumptions, operating objectives and project horizon.', action:'Build a transparent option model before selecting the preferred system.' }
    ]
  };

  const state = { heroAudience:'residential', heroIssue:'bills', formAudience:'', formIssue:'', formContext:'', step:1 };
  const qs = (selector, root=document) => root.querySelector(selector);
  const qsa = (selector, root=document) => [...root.querySelectorAll(selector)];
  const getIssue = (audience, id) => (issueData[audience] || issueData.residential).find(item => item.id === id) || (issueData[audience] || issueData.residential)[0];

  function initMobileMenu() {
    const toggle = qs('[data-mobile-toggle]');
    const menu = qs('[data-mobile-menu]');
    if (!toggle || !menu) return;

    const closeMenu = (restoreFocus = false) => {
      menu.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open navigation');
      if (restoreFocus) toggle.focus();
    };

    toggle.addEventListener('click', () => {
      const open = menu.classList.toggle('open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
      if (open) qs('input, a, button', menu)?.focus();
    });
    qsa('a, button', menu).forEach(item => item.addEventListener('click', () => closeMenu(false)));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.classList.contains('open')) closeMenu(true);
    });
    document.addEventListener('click', event => {
      if (menu.classList.contains('open') && !menu.contains(event.target) && !toggle.contains(event.target)) closeMenu(false);
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1400 && menu.classList.contains('open')) closeMenu(false);
    });
  }

  function initHeaderDisclosures() {
    const disclosures = qsa('.me2-site-header .me2-nav-group, .me2-site-header [data-header-search]');
    if (!disclosures.length) return;

    const closeDisclosure = (details, restoreFocus = false) => {
      if (!details.open) return;
      details.open = false;
      if (restoreFocus) qs(':scope > summary', details)?.focus();
    };

    disclosures.forEach(details => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        disclosures.forEach(other => { if (other !== details) closeDisclosure(other); });
        if (details.matches('[data-header-search]')) {
          window.setTimeout(() => qs('input[type="search"]', details)?.focus(), 0);
        }
      });
      qsa('a', details).forEach(link => link.addEventListener('click', () => closeDisclosure(details)));
    });

    document.addEventListener('click', event => {
      disclosures.forEach(details => {
        if (details.open && !details.contains(event.target)) closeDisclosure(details);
      });
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const openDisclosure = disclosures.find(details => details.open);
      if (openDisclosure) closeDisclosure(openDisclosure, true);
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth <= 1400) disclosures.forEach(details => closeDisclosure(details));
    });
  }

  function initHeroDiagnostic() {
    const heroIssues = qs('#heroIssues');
    const analysisTitle = qs('#analysisTitle');
    const analysisBody = qs('#analysisBody');
    const signalBars = qs('#signalBars');
    if (!heroIssues || !analysisTitle || !analysisBody || !signalBars) return;

    const renderAnalysis = () => {
      const item = getIssue(state.heroAudience, state.heroIssue);
      analysisTitle.textContent = item.title;
      analysisBody.textContent = item.body;
      signalBars.innerHTML = item.signals.map(signal => `<div class="signal-row"><span>${signal[0]}</span><div class="signal-track"><div class="signal-fill" style="--w:${signal[1]}%"></div></div></div>`).join('');
    };

    const renderIssues = () => {
      const items = issueData[state.heroAudience];
      if (!items.some(item => item.id === state.heroIssue)) state.heroIssue = items[0].id;
      heroIssues.innerHTML = items.map(item => `<button class="issue-button ${item.id === state.heroIssue ? 'active' : ''}" type="button" data-hero-issue="${item.id}">${item.label}</button>`).join('');
      qsa('[data-hero-issue]', heroIssues).forEach(button => button.addEventListener('click', () => {
        state.heroIssue = button.dataset.heroIssue;
        renderIssues();
        renderAnalysis();
      }));
    };

    qsa('[data-audience]').forEach(button => button.addEventListener('click', () => {
      state.heroAudience = button.dataset.audience;
      state.heroIssue = issueData[state.heroAudience][0].id;
      qsa('[data-audience]').forEach(tab => {
        const active = tab.dataset.audience === state.heroAudience;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      renderIssues();
      renderAnalysis();
    }));

    renderIssues();
    renderAnalysis();
  }

  function initDiagnosticModal() {
    const modal = qs('#diagnosticModal');
    if (!modal) return;
    const modalClose = qs('#modalClose', modal);
    const audienceChoices = qs('#audienceChoices', modal);
    const modalIssueChoices = qs('#modalIssueChoices', modal);
    const nextButton = qs('#nextButton', modal);
    const backButton = qs('#backButton', modal);
    const progressBar = qs('#progressBar', modal);
    const spendLabel = qs('#spendLabel', modal);
    const resultTitle = qs('#resultTitle', modal);
    const resultBody = qs('#resultBody', modal);
    const resultEvidence = qs('#resultEvidence', modal);
    const resultAction = qs('#resultAction', modal);
    const audienceInput = qs('[name="contact[Customer type]"]', modal);
    const issueInput = qs('[name="contact[Energy problem]"]', modal);
    const summaryInput = qs('[name="contact[Preliminary diagnostic]"]', modal);
    const sourceInput = qs('[name="contact[Source]"]', modal);
    const submitButton = qs('[data-diagnostic-submit]', modal);
    const form = qs('#EnergyDiagnosticForm', modal);
    const formStatus = form ? qs('[data-success]', form) : null;
    let lastFocused = null;

    const setPageInert = active => {
      qsa('.me2-heat-alert, [data-site-header], #MainContent, .me2-site-footer').forEach(element => {
        if (active) element.setAttribute('inert', '');
        else element.removeAttribute('inert');
      });
    };

    const renderAudienceChoices = () => qsa('[data-form-audience]', audienceChoices).forEach(button => {
      const active = button.dataset.formAudience === state.formAudience;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    const renderModalIssues = () => {
      if (!state.formAudience) { modalIssueChoices.innerHTML = ''; return; }
      modalIssueChoices.innerHTML = issueData[state.formAudience].map(item => `<button class="choice ${item.id === state.formIssue ? 'active' : ''}" type="button" data-modal-issue="${item.id}" aria-pressed="${item.id === state.formIssue ? 'true' : 'false'}">${item.label}</button>`).join('');
      qsa('[data-modal-issue]', modalIssueChoices).forEach(button => button.addEventListener('click', () => {
        state.formIssue = button.dataset.modalIssue;
        renderModalIssues();
        updateControls();
      }));
    };

    const renderResult = () => {
      const item = getIssue(state.formAudience || 'residential', state.formIssue);
      resultTitle.textContent = item.title;
      resultBody.textContent = item.body;
      resultEvidence.textContent = item.evidence;
      resultAction.textContent = item.action;
      if (audienceInput) audienceInput.value = state.formContext === 'industrial' ? 'Industrial' : (state.formAudience === 'commercial' ? 'Business' : 'Home');
      if (issueInput) issueInput.value = item.label;
      if (summaryInput) summaryInput.value = `${item.title} Evidence: ${item.evidence} Recommended first action: ${item.action}`;
      if (sourceInput) sourceInput.value = state.formContext ? `Website energy assessment — ${state.formContext}` : 'Website energy assessment';
    };

    const updateControls = () => {
      if (state.step === 1) nextButton.disabled = !state.formAudience;
      else if (state.step === 2) nextButton.disabled = !state.formIssue;
      else nextButton.disabled = false;
    };

    const updateStep = (focusHeading = false) => {
      qsa('.form-step', modal).forEach(step => {
        const active = Number(step.dataset.step) === state.step;
        step.classList.toggle('active', active);
        step.setAttribute('aria-hidden', String(!active));
      });
      progressBar.style.width = `${state.step * 25}%`;
      backButton.disabled = state.step === 1;
      nextButton.hidden = state.step === 4;
      if (submitButton) submitButton.hidden = state.step !== 4;
      if (state.step === 2) renderModalIssues();
      if (state.step === 3 && spendLabel) spendLabel.innerHTML = (state.formAudience === 'commercial' ? 'Approximate monthly energy spend' : 'Approximate quarterly electricity bill') + ' <span>optional</span>';
      if (state.step === 4) renderResult();
      updateControls();
      if (focusHeading) {
        const heading = qs('.form-step.active h2', modal);
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      }
    };

    const openModal = (presetAudience, presetIssue, audienceWasSpecified = false, presetContext = '') => {
      lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      state.step = 1;
      state.formAudience = audienceWasSpecified ? presetAudience : (presetAudience || state.heroAudience || '');
      state.formIssue = presetIssue || (state.formAudience ? issueData[state.formAudience][0].id : '');
      state.formContext = presetContext;
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
      setPageInert(true);
      renderAudienceChoices();
      renderModalIssues();
      updateStep();
      setTimeout(() => modalClose?.focus(), 50);
    };

    const closeModal = () => {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      setPageInert(false);
      const focusTarget = lastFocused && document.contains(lastFocused)
        ? lastFocused
        : (qs('.js-open-diagnostic') || qs('#MainContent'));
      focusTarget?.focus();
    };

    document.addEventListener('click', event => {
      const button = event.target.closest('.js-open-diagnostic');
      if (!button) return;
      const audienceWasSpecified = button.hasAttribute('data-open-audience');
      const preset = audienceWasSpecified ? button.dataset.openAudience : state.heroAudience;
      const presetIssue = button.hasAttribute('data-open-issue') ? button.dataset.openIssue : (preset === state.heroAudience ? state.heroIssue : '');
      openModal(preset, presetIssue, audienceWasSpecified, button.dataset.openContext || '');
    });
    modalClose?.addEventListener('click', closeModal);
    modal.addEventListener('click', event => { if (event.target === modal) closeModal(); });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && modal.classList.contains('open')) closeModal(); });
    modal.addEventListener('keydown', event => {
      if (event.key !== 'Tab' || !modal.classList.contains('open')) return;
      const focusable = qsa('a[href], button:not([disabled]):not([hidden]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', modal)
        .filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    qsa('[data-form-audience]', audienceChoices).forEach(button => button.addEventListener('click', () => {
      state.formAudience = button.dataset.formAudience;
      state.formIssue = issueData[state.formAudience][0].id;
      state.formContext = '';
      renderAudienceChoices();
      renderModalIssues();
      updateControls();
    }));

    nextButton?.addEventListener('click', () => {
      if (nextButton.disabled || state.step >= 4) return;
      state.step += 1;
      updateStep(true);
    });
    backButton?.addEventListener('click', () => {
      if (state.step <= 1) return;
      state.step -= 1;
      updateStep(true);
    });

    if (form) {
      form.addEventListener('submit', event => {
        renderResult();
        const email = qs('[name="contact[email]"]', form);
        if (!email || !email.value.trim()) {
          event.preventDefault();
          email?.focus();
          email?.setAttribute('aria-invalid','true');
        }
      });
      if (formStatus && (formStatus.dataset.success === 'true' || formStatus.dataset.errors === 'true')) {
        lastFocused = qs('.js-open-diagnostic') || qs('#MainContent');
        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
        setPageInert(true);
        state.step = 4;
        updateStep(false);
        const returnedFeedback = qs('.me-form-errors, .me-form-success', form);
        if (returnedFeedback) {
          returnedFeedback.setAttribute('tabindex', '-1');
          window.setTimeout(() => returnedFeedback.focus(), 50);
        }
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initHeaderDisclosures();
    initHeroDiagnostic();
    initDiagnosticModal();
  });
})();


